import { img } from '@/lib/img';
import { cn } from '@/lib/cn';

type Props = {
  /** Path to the JPG (also serves as fallback). The WebP at the same path is preferred. */
  src: string;
  alt: string;
  className?: string;
  imgClassName?: string;
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
  sizes?: string;
  /** Stops the broken-image icon if the file is missing on disk. */
  hideOnError?: boolean;
};

/**
 * <picture>-wrapped image that prefers WebP and falls back to the JPG.
 * - Both sources go through the cache-buster helper so a content swap
 *   doesn't get stuck behind browser cache.
 */
export function Picture({
  src,
  alt,
  className,
  imgClassName,
  loading = 'lazy',
  fetchPriority = 'auto',
  decoding = 'async',
  sizes,
  hideOnError,
}: Props) {
  const webp = img(src.replace(/\.(jpe?g|png)$/i, '.webp'));
  const fallback = img(src);
  return (
    <picture className={className}>
      <source srcSet={webp} type="image/webp" sizes={sizes} />
      <img
        src={fallback}
        alt={alt}
        loading={loading}
        decoding={decoding}
        // @ts-expect-error - lowercase attr for legacy DOM checks
        fetchpriority={fetchPriority}
        sizes={sizes}
        className={cn(imgClassName)}
        onError={
          hideOnError
            ? (e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }
            : undefined
        }
      />
    </picture>
  );
}
